import React, { useId, useState } from 'react';
import classNames from 'classnames';
import styles from './exclusive-buttons.module.scss';


export interface ExclusiveButtonsProps<T, K = T> {
  items: readonly T[];
  value?: K;
  onChange?: (value: K) => void;
  onBlur?: () => void;
  identityFn?: (item: T) => K;
  textFn?: (item: T) => React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
  className?: string;
  renderItem?: (item: T) => React.ReactNode;
}

export function ExclusiveButtons<T, K = T>({
  items,
  value,
  onChange,
  onBlur,
  identityFn = (item) => item as unknown as K,
  textFn = (item) => String(item),
  disabled = false,
  readonly = false,
  className,
  renderItem
}: ExclusiveButtonsProps<T, K>) {
  const [focusedValue, setFocusedValue] = useState<K | null>(null);
  const name = useId();

  return (
    <div className={classNames(styles.exclusiveButtons, className)}>
      {items.map((item) => {
        const itemValue = identityFn(item);
        const isSelected = itemValue === value;
        const isFocused = itemValue === focusedValue;

        return (
          <label
            key={String(itemValue)}
            className={classNames('control button', styles.button, {
              'is-primary': isSelected,
              'is-selected': isSelected,
              'is-focused': isFocused,
            })}
            aria-disabled={disabled || readonly}
          >
            <input
              type="radio"
              name={name}
              className={styles.visuallyHidden}
              value={String(itemValue)}
              checked={isSelected}
              disabled={disabled || readonly}
              onChange={() => onChange?.(itemValue)}
              onFocus={() => setFocusedValue(itemValue)}
              onBlur={() => {
                setFocusedValue(null);
                onBlur?.();
              }}
            />
            {renderItem ? renderItem(item) : textFn(item)}
          </label>
        );
      })}
    </div>
  );
}
